// src/utils/auth.js

/**
 * Récupère l'objet utilisateur depuis sessionStorage ou localStorage
 * en essayant plusieurs clés connues.
 */
export const getUser = () => {
  const keys = ["userData", "user", "authUser", "user_data", "currentUser"];

  for (const key of keys) {
    const rawSession = sessionStorage.getItem(key);
    if (rawSession) {
      try {
        return JSON.parse(rawSession);
      } catch (e) {
        // pas du JSON, on ignore
      }
    }
    const rawLocal = localStorage.getItem(key);
    if (rawLocal) {
      try {
        return JSON.parse(rawLocal);
      } catch (e) {
        // pas du JSON, on ignore
      }
    }
  }

  return null;
};

/**
 * Récupère les rôles de l'utilisateur depuis sessionStorage.
 * Clé : "userRoles" (stockée par Signin2.jsx)
 */
export const getUserRoles = () => {
  const keys = ["userRoles", "user_roles", "roles"];

  for (const key of keys) {
    const rawSession = sessionStorage.getItem(key);
    if (rawSession) {
      try {
        const parsed = JSON.parse(rawSession);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {}
    }
    const rawLocal = localStorage.getItem(key);
    if (rawLocal) {
      try {
        const parsed = JSON.parse(rawLocal);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {}
    }
  }
  return [];
};

/**
 * Récupère le role_id de l'utilisateur connecté.
 * Cherche dans plusieurs emplacements possibles.
 */
export const getUserRoleId = () => {
  // 1) Chercher directement dans sessionStorage/localStorage
  const directKeys = ["role_id", "userRoleId", "user_role_id"];
  for (const key of directKeys) {
    const val = sessionStorage.getItem(key) || localStorage.getItem(key);
    if (val) {
      const parsed = parseInt(val, 10);
      if (!isNaN(parsed)) {
        console.log("🎯 role_id trouvé via clé directe:", key, "=>", parsed);
        return parsed;
      }
    }
  }

  // 2) Chercher dans l'objet user (userData)
  const user = getUser();
  if (user) {
    const candidates = [
      { path: "user.role_id", value: user.role_id },
      { path: "user.user_role_id", value: user.user_role_id },
      { path: "user.roleId", value: user.roleId },
      { path: "user.user_role?.role_id", value: user.user_role?.role_id },
      { path: "user.role?.role_id", value: user.role?.role_id },
      { path: "user.user_roles?.[0]?.role_id", value: user.user_roles?.[0]?.role_id },
      { path: "user.roles?.[0]?.role_id", value: user.roles?.[0]?.role_id },
      { path: "user.roles?.[0]?.id", value: user.roles?.[0]?.id },
    ];

    for (const c of candidates) {
      if (c.value !== undefined && c.value !== null) {
        const parsed = parseInt(c.value, 10);
        if (!isNaN(parsed)) {
          console.log(`🎯 role_id trouvé via ${c.path} => ${parsed}`);
          return parsed;
        }
      }
    }
  }

  // 3) Fallback : chercher dans userRoles (tableau)
  const userRoles = getUserRoles();
  if (Array.isArray(userRoles) && userRoles.length > 0) {
    for (const r of userRoles) {
      const val = r?.role_id || r?.id;
      if (val !== undefined && val !== null) {
        const parsed = parseInt(val, 10);
        if (!isNaN(parsed)) {
          console.log("🎯 role_id trouvé via userRoles =>", parsed);
          return parsed;
        }
      }
    }
  }

  console.warn("⚠️ Aucun role_id trouvé dans le sessionStorage");
  return null;
};

/**
 * Récupère l'id_marche de l'utilisateur connecté.
 */
export const getUserMarcheId = () => {
  const directKeys = ["id_marche", "marche_id", "user_marche_id"];
  for (const key of directKeys) {
    const val = sessionStorage.getItem(key) || localStorage.getItem(key);
    if (val) return val;
  }

  const user = getUser();
  if (!user) return null;

  const candidates = [
    user.id_marche,
    user.marche_id,
    user.marche?.id_marche,
    user.user_marche?.id_marche,
  ];

  for (const c of candidates) {
    if (c !== undefined && c !== null) return c;
  }
  return null;
};

/**
 * ✅ Helpers de rôle
 */
export const isAdminEntite = () => getUserRoleId() === 1002;
export const isAdminPrincipal = () => getUserRoleId() === 1003;
export const isAgent = () => {
  const id = getUserRoleId();
  return id === 1012 || id === 1013;
};

/**
 * ✅ NOUVEAU : Retourne true si l'utilisateur est Super Admin.
 * Détection via userRoles (name === 'super_admin') OU role_id si applicable.
 */
/**
 * ✅ Retourne true UNIQUEMENT si l'utilisateur est Super Admin.
 * Détection stricte via userRoles (name === 'super_admin').
 * Aucun fallback sur role_id pour éviter les faux positifs.
 */
export const isSuperAdmin = () => {
  const roles = getUserRoles();
  if (!Array.isArray(roles) || roles.length === 0) {
    return false;
  }

  // On vérifie STRICTEMENT le nom du rôle
  const hasSuperAdmin = roles.some((r) => {
    const name = (r?.name || "").toString().trim().toLowerCase();
    return name === "super_admin" || name === "superadmin" || name === "super admin";
  });

  return hasSuperAdmin;
};

/**
 * ✅ Retourne true si l'utilisateur peut filtrer par périmètre marché.
 * Admin Entité (1002) OU Super Admin.
 */
export const canFilterByMarketScope = () => {
  const roleId = getUserRoleId();
  return roleId === 1002 || isSuperAdmin();
};